import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Stethoscope, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api, openPdf, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { PatientFormDialog } from "@/components/PatientFormDialog";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-border/60 text-sm">
    <span className="text-muted-foreground">{k}</span>
    <span className="font-medium text-right">{v || "—"}</span>
  </div>
);

export default function PatientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = () => api.get(`/patients/${id}`).then(({ data }) => setP(data));
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); /* eslint-disable-next-line */ }, [id]);

  if (!p) return <p className="text-sm text-muted-foreground">Loading case sheet…</p>;

  return (
    <div className="space-y-5" data-testid="patient-detail-page">
      <Button variant="ghost" size="sm" onClick={() => nav("/patients")} className="gap-2 -ml-2">
        <ArrowLeft className="h-4 w-4" /> Back to patients
      </Button>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary">{p.uhid}</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{p.name}</h1>
          <p className="text-sm text-muted-foreground">{p.age} yrs · {p.gender} · {p.phone || "no phone"}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" data-testid="edit-patient-button"
            onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4" /> Edit record
          </Button>
          <Button className="gap-2" data-testid="new-consultation-button"
            onClick={() => nav(`/consultation/new?patient=${p.id}`)}>
            <Stethoscope className="h-4 w-4" /> New consultation
          </Button>
        </div>
      </div>

      <PatientFormDialog open={editOpen} onOpenChange={setEditOpen} patient={p}
        facilityId={p.facility_id} onSaved={load} />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Demographics</CardTitle></CardHeader>
          <CardContent>
            <Row k="Blood group" v={p.blood_group} /><Row k="DOB" v={p.dob} />
            <Row k="Marital status" v={p.marital_status} /><Row k="Occupation" v={p.occupation} />
            <Row k="BMI" v={p.bmi} /><Row k="Insurance" v={p.insurance} />
            <Row k="Address" v={p.address} /><Row k="Emergency" v={p.emergency_contact} />
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Clinical background</CardTitle></CardHeader>
          <CardContent>
            <Row k="Allergies" v={p.allergies} /><Row k="Past history" v={p.past_history} />
            <Row k="Surgical" v={p.surgical_history} /><Row k="Family" v={p.family_history} />
            <Row k="Drugs" v={p.drug_history} /><Row k="Smoking" v={p.smoking} />
            <Row k="Alcohol" v={p.alcohol} /><Row k="Tobacco" v={p.tobacco} />
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Billing summary</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {p.bills.length === 0 && <p className="text-sm text-muted-foreground">No bills raised.</p>}
            {p.bills.map((b) => (
              <div key={b.id} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5">
                <span className="font-mono text-xs">{b.number}</span>
                <span className="tabular-nums">₹{b.total}</span>
                <Badge variant={b.status === "Paid" ? "secondary" : "destructive"}>{b.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Visit timeline</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {p.consultations.length === 0 && <p className="text-sm text-muted-foreground">No consultations recorded.</p>}
          {p.consultations.map((c) => {
            const rx = p.prescriptions.find((r) => r.consultation_id === c.id);
            return (
              <div key={c.id} className="border-l-2 border-primary pl-4 py-1" data-testid={`timeline-${c.id}`}>
                <p className="text-xs font-mono text-muted-foreground">
                  {new Date(c.created_at).toLocaleString()}
                </p>
                <p className="font-medium text-sm">{c.diagnosis || "Consultation"}</p>
                <p className="text-sm text-muted-foreground">{c.complaints}</p>
                <div className="flex gap-2 mt-1.5">
                  <Button size="sm" variant="outline" onClick={() => nav(`/consultation/${c.id}`)}>Open</Button>
                  {rx && (
                    <Button size="sm" variant="outline" className="gap-1.5"
                      data-testid={`timeline-pdf-${c.id}`} onClick={() => openPdf(`/prescriptions/${rx.id}/pdf`)}>
                      <FileText className="h-3.5 w-3.5" /> {rx.number}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
